import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getSettings, 
  updateSetting, 
  updateMultipleSettings,
  initializeDatabase,
  resetSettingsToDefault,
  exportSettings,
  importSettings
} from '../services/settingsService';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    // Try to load from localStorage first for faster initial render
    try {
      const cached = localStorage.getItem('app_settings');
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      console.error('Error loading cached settings:', error);
      return {};
    }
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(() => {
    return localStorage.getItem('settings_last_fetch') || '0';
  });

  // Fetch settings from the database
  const fetchSettings = async (force = false) => {
    try {
      setIsLoading(true);
      setError(null);

      // Check if we need to fetch (cache for 5 minutes)
      const now = Date.now();
      const lastCheck = parseInt(lastFetch);
      
      if (!force && now - lastCheck < 5 * 60 * 1000 && Object.keys(settings).length > 0) {
        console.log('Using cached settings');
        setIsLoading(false);
        return settings;
      }

      console.log('Fetching fresh settings from database...');
      const data = await getSettings();
      
      if (data) {
        setSettings(data);
        // Cache in localStorage
        localStorage.setItem('app_settings', JSON.stringify(data));
        localStorage.setItem('settings_last_fetch', now.toString());
        setLastFetch(now.toString());
        
        // Apply theme colors to CSS variables
        applyThemeColors(data);
        
        return data;
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError(err.message || 'Failed to fetch settings');
    } finally {
      setIsLoading(false);
    }
  };

  // Apply theme colors to CSS variables
  const applyThemeColors = (settingsData) => {
    try {
      const root = document.documentElement;
      
      // Apply color settings
      if (settingsData.primary_color) {
        root.style.setProperty('--primary-color', settingsData.primary_color);
      }
      if (settingsData.secondary_color) {
        root.style.setProperty('--secondary-color', settingsData.secondary_color);
      }
      if (settingsData.accent_color) {
        root.style.setProperty('--accent-color', settingsData.accent_color);
      }
      if (settingsData.background_color) {
        root.style.setProperty('--background-color', settingsData.background_color);
      }
      if (settingsData.text_color) {
        root.style.setProperty('--text-color', settingsData.text_color);
      }
      
      // Apply header & footer colors
      if (settingsData.header_background) {
        root.style.setProperty('--header-background', settingsData.header_background);
      }
      if (settingsData.footer_background) {
        root.style.setProperty('--footer-background', settingsData.footer_background);
      }
      if (settingsData.header_text_color) {
        root.style.setProperty('--header-text-color', settingsData.header_text_color);
      }
      if (settingsData.footer_text_color) {
        root.style.setProperty('--footer-text-color', settingsData.footer_text_color);
      }
      
      // Apply button colors
      if (settingsData.button_primary_color) {
        root.style.setProperty('--button-primary-color', settingsData.button_primary_color);
      }
      if (settingsData.button_secondary_color) {
        root.style.setProperty('--button-secondary-color', settingsData.button_secondary_color);
      }
      if (settingsData.button_text_color) {
        root.style.setProperty('--button-text-color', settingsData.button_text_color);
      }
      
      // Apply form colors
      if (settingsData.form_background) {
        root.style.setProperty('--form-background', settingsData.form_background);
      }
      if (settingsData.form_border_color) {
        root.style.setProperty('--form-border-color', settingsData.form_border_color);
      }
      if (settingsData.form_focus_color) {
        root.style.setProperty('--form-focus-color', settingsData.form_focus_color);
      }
    } catch (error) {
      console.error('Error applying theme colors:', error);
    }
  };

  // Update a single setting
  const updateSettingValue = async (key, value, valueType = 'string') => {
    try {
      const success = await updateSetting(key, value, valueType);
      
      if (success) {
        // Update local state
        const updatedSettings = { ...settings, [key]: value };
        setSettings(updatedSettings);
        
        // Update cache
        localStorage.setItem('app_settings', JSON.stringify(updatedSettings));
        
        // Apply theme if color setting changed
        if (key.includes('color') || key.includes('background')) {
          applyThemeColors(updatedSettings);
        }
        
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Error updating setting:', err);
      setError(err.message || 'Failed to update setting');
      return false;
    }
  };

  // Update multiple settings at once
  const updateMultipleSettingsValues = async (newSettings) => {
    try {
      const success = await updateMultipleSettings(newSettings);
      
      if (success) {
        // Update local state
        const updatedSettings = { ...settings, ...newSettings };
        setSettings(updatedSettings);
        
        // Update cache
        localStorage.setItem('app_settings', JSON.stringify(updatedSettings));
        
        // Apply theme colors
        applyThemeColors(updatedSettings);
        
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Error updating multiple settings:', err);
      setError(err.message || 'Failed to update settings');
      return false;
    }
  };

  // Reset settings to default
  const resetSettings = async () => {
    try {
      setIsLoading(true);
      const success = await resetSettingsToDefault();
      
      if (success) {
        // Refetch settings
        await fetchSettings(true);
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Error resetting settings:', err);
      setError(err.message || 'Failed to reset settings');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Export settings to JSON file
  const exportSettingsToFile = async () => {
    try {
      return await exportSettings();
    } catch (err) {
      console.error('Error exporting settings:', err);
      setError(err.message || 'Failed to export settings');
      return false;
    }
  };

  // Import settings from JSON file
  const importSettingsFromFile = async (file) => {
    try {
      setIsLoading(true);
      const success = await importSettings(file);
      
      if (success) {
        // Refetch settings
        await fetchSettings(true);
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Error importing settings:', err);
      setError(err.message || 'Failed to import settings');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize database and fetch settings on mount
  useEffect(() => {
    const initializeAndFetch = async () => {
      try {
        // Initialize database with default settings if needed
        await initializeDatabase();
        
        // Fetch settings
        await fetchSettings();
      } catch (err) {
        console.error('Error initializing settings:', err);
        setError(err.message || 'Failed to initialize settings');
        setIsLoading(false);
      }
    };

    initializeAndFetch();
  }, []);

  // Get a specific setting value with optional default
  const getSetting = (key, defaultValue = null) => {
    return settings[key] !== undefined ? settings[key] : defaultValue;
  };

  // Check if a feature is enabled
  const isFeatureEnabled = (featureKey) => {
    return settings[featureKey] === true || settings[featureKey] === 'true';
  };

  // Clear cache and refetch
  const clearCacheAndRefetch = async () => {
    localStorage.removeItem('app_settings');
    localStorage.removeItem('settings_last_fetch');
    setLastFetch('0');
    return await fetchSettings(true);
  };

  const value = {
    settings,
    isLoading,
    error,
    fetchSettings,
    updateSetting: updateSettingValue,
    updateMultipleSettings: updateMultipleSettingsValues,
    resetSettings,
    exportSettings: exportSettingsToFile,
    importSettings: importSettingsFromFile,
    getSetting,
    isFeatureEnabled,
    clearCacheAndRefetch,
    refreshSettings: () => fetchSettings(true)
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;

